<template>
  <div>
    <v-container fluid>
      <!-- Header -->
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar dark class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">Account Info Management</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2" style="margin-top: 1rem">
        <v-container class="px-6 py-6">
          <!-- Filters -->
          <v-row class="mb-4">
            <v-col cols="12" sm="6" md="3">
              <v-text-field v-model="search" label="Search" prepend-inner-icon="mdi-magnify" variant="outlined" rounded class="search-field" clearable hide-details />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-select v-model="categoryFilter" label="Category" :items="categories" variant="outlined" rounded class="search-field" clearable hide-details />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-select v-model="subCategoryFilter" label="Subcategory" :items="subCategories" variant="outlined" rounded class="search-field" clearable hide-details />
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-select v-model="customerScopeFilter" :label="accountInfoText('customerScope')" :items="customerScopeOptions" variant="outlined" rounded class="search-field" hide-details />
            </v-col>
            <v-col cols="12" lg="3" class="d-flex justify-end align-center">
              <div class="d-flex flex-column flex-sm-row gap-2 w-100">
                <v-btn v-if="permissions.edit" class="btn-category flex-grow-1 flex-sm-grow-0" @click="openCategoriesDialog()">
                  <v-icon start>mdi-shape</v-icon>
                  Categories
                </v-btn>
                <v-btn v-if="permissions.add" class="btn-layout flex-grow-1 flex-sm-grow-0" @click="openDialog()">
                  <v-icon start>mdi-plus</v-icon>
                  Add Account
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <!-- Data Table -->
          <v-data-table :headers="headers" :items="filteredItems" :loading="loading" class="elevation-1 my-data-table modern-table" density="comfortable" :items-per-page="10">
            <template v-slot:item.categoryID="{ item }">
              <div class="category-scope-cell">
                <v-chip :color="item.categoryID ? getCategoryColorById(item.categoryID) : 'grey'" size="small" variant="outlined">
                  <span class="font-weight-medium">{{ item.categoryID ? getCategoryNameById(item.categoryID) : 'No Category' }}</span>
                  <span v-if="item.subCategoryID" class="text-grey-darken-1">
                    / {{ getSubCategoryNameById(item.subCategoryID) }}
                  </span>
                </v-chip>
                <v-chip :color="getCustomerScopeColor(item)" size="small" variant="outlined" class="customer-scope-chip">
                  {{ getCustomerScopeText(item.isNewUser) }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.effectivePeriod="{ item }">
              <span class="text-body-2">
                {{ formatEffectivePeriod(item.effectiveStartDate, item.effectiveEndDate) }}
              </span>
            </template>

            <template v-slot:item.isActive="{ item }">
              <v-chip :color="item.isActive ? 'success' : 'error'" size="small" variant="outlined">
                {{ item.isActive ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>
            <!-- NEW: Duplicate Column -->
            <template v-slot:item.duplicate="{ item }">
              <div class="d-flex justify-center">
                <v-btn v-if="permissions.add" size="small" variant="flat" @click="openDuplicateDialog(item)" class="action-btn duplicate-btn">
                  <v-icon size="18">mdi-content-copy</v-icon>
                  <v-tooltip activator="parent" location="top">Create a duplicate of Account Info</v-tooltip>
                </v-btn>
              </div>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="action-buttons-group">
                <v-btn size="small" variant="outlined" color="info" @click="viewTiers(item)" class="action-btn">
                  <v-icon size="16">mdi-view-list</v-icon>
                  <v-tooltip activator="parent" location="top">View Tiers</v-tooltip>
                </v-btn>
                <v-btn size="small" variant="outlined" color="primary" @click="openDialog(item)" class="action-btn">
                  <v-icon v-if="!permissions.edit" size="16" color="primary">mdi-eye-outline</v-icon>
                  <v-img v-else :src="images.EditImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">{{ permissions.edit ? 'Edit' : 'View' }}</v-tooltip>
                </v-btn>
                <v-btn v-if="permissions.delete" size="small" variant="outlined" color="error" @click="confirmDelete(item)" class="action-btn">
                  <v-img :src="images.DeleteImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-container>
      </v-card>
    </v-container>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card class="add-proactive-dialog">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="d-flex align-center">
            <div class="header-icon-container">
              <v-avatar class="icon-background" size="46">
                <v-icon color="#4b2667" size="24">
                  {{ editMode ? 'mdi-pencil' : 'mdi-plus' }}
                </v-icon>
              </v-avatar>
            </div>
            <div>
              <div class="dialog-title">
                {{ editMode ? (permissions.edit ? 'Edit Account Info' : 'View Account Info') : 'Create Account Info' }}
              </div>
              <div class="dialog-subtitle">
                {{ editMode ? 'Update account information' : 'Add new account information' }}
              </div>
            </div>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="form" v-model="formValid">
            <div class="form-section">
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="formData.accountCode" label="Account Code*" variant="outlined" density="comfortable" :rules="[rules.required]" hide-details="auto" />
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="formData.accountName" label="Account Name*" variant="outlined" density="comfortable" :rules="[rules.required]" hide-details="auto" />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="formData.categoryID"
                    label="Category*"
                    :items="formCategories"
                    item-title="title"
                    item-value="value"
                    variant="outlined"
                    density="comfortable"
                    :rules="[rules.required]"
                    hide-details="auto"
                    :loading="loadingCategories"
                    @update:model-value="onCategoryChange"
                  />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="formData.subCategoryID"
                    label="Subcategory"
                    :items="formSubCategories"
                    item-title="title"
                    item-value="value"
                    variant="outlined"
                    density="comfortable"
                    :disabled="!formData.categoryID"
                    hide-details="auto"
                    :loading="loadingSubCategories"
                    clearable
                  />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="formData.calculationMethod"
                    label="Calculation method"
                    :items="formCalculationMethods"
                    item-title="title"
                    item-value="value"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    clearable
                  />
                </v-col>
                <v-col cols="12">
                  <div class="customer-scope-toggle">
                    <div>
                      <div class="customer-scope-title">{{ getCustomerScopeToggleTitle(formData.isNewUser) }}</div>
                      <div class="customer-scope-description">{{ getCustomerScopeDescription(formData.isNewUser) }}</div>
                    </div>
                    <v-switch v-model="formData.isNewUser" color="primary" inset hide-details />
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-textarea v-model="formData.description" label="Description" variant="outlined" density="comfortable" rows="3" hide-details="auto" />
                </v-col>
                <v-col cols="6">
                  <v-menu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ props }">
                      <v-text-field
                        :model-value="formatDateDisplay(formData.effectiveStartDate)"
                        label="Effective Start Date"
                        variant="outlined"
                        density="comfortable"
                        hide-details="auto"
                        readonly
                        v-bind="props"
                        clearable
                        @click:clear="formData.effectiveStartDate = null"
                      >
                        <template v-slot:append-inner>
                          <v-icon color="primary">mdi-calendar</v-icon>
                        </template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      v-model="formData.effectiveStartDate"
                      @update:model-value="onStartDateChange"
                      color="primary"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <v-menu v-model="endDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ props }">
                      <v-text-field
                        :model-value="formatDateDisplay(formData.effectiveEndDate)"
                        label="Effective End Date"
                        variant="outlined"
                        density="comfortable"
                        hide-details="auto"
                        readonly
                        v-bind="props"
                        clearable
                        @click:clear="formData.effectiveEndDate = null"
                      >
                        <template v-slot:append-inner>
                          <v-icon color="primary">mdi-calendar</v-icon>
                        </template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      v-model="formData.effectiveEndDate"
                      @update:model-value="onEndDateChange"
                      :min="formData.effectiveStartDate"
                      color="primary"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12">
                  <v-checkbox v-model="formData.isActive" label="Active" color="primary" hide-details />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="closeDialog">{{ isViewOnly ? 'Close' : 'Cancel' }}</v-btn>
          <v-btn v-if="editMode ? permissions.edit : permissions.add" class="btn-save" :loading="saving" :disabled="!formValid" @click="saveAccountInfo">
            {{ editMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="duplicateDialog" max-width="600px" persistent>
      <v-card class="add-proactive-dialog">
        <v-card-title class="dialog-header dialog-header-add d-flex align-center">
          <div class="d-flex align-center w-100">
            <div class="flex-grow-1">
              <div class="dialog-title">Duplicate Account Info</div>
            </div>
            <v-btn icon variant="text" @click="closeDuplicateDialog" size="small">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content pt-6">
          <v-form ref="duplicateForm" v-model="duplicateFormValid">
            <!-- Interest Rate Name -->
            <div class="form-section">
              <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                Set a Account Info Code <span style="color: #E74C3C;">*</span>
              </label>
              <v-text-field
                v-model="duplicateFormData.accountCode"
                placeholder="Testing Duplicate"
                variant="outlined"
                density="comfortable"
                :rules="[rules.required]"
                class="custom-rounded"
                hide-details="auto"
              />
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="dialog-content pt-0 pb-4">
          <v-spacer></v-spacer>
          <v-btn class="btn-close" @click="closeDuplicateDialog" :disabled="savingDuplicate">
            <v-icon start>mdi-close</v-icon>
            Close
          </v-btn>
          <v-btn v-if="permissions.add" class="btn-save" @click="saveDuplicate" :loading="savingDuplicate" :disabled="!duplicateFormValid">
            <v-icon start>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Tiers Dialog -->
    <v-dialog v-model="tiersDialog" max-width="1000px">
      <v-card class="add-proactive-dialog">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="d-flex align-center justify-space-between w-100">
            <div class="d-flex align-center">
              <div class="header-icon-container">
                <v-avatar class="icon-background" size="46">
                  <v-icon color="#4b2667" size="24">mdi-view-list</v-icon>
                </v-avatar>
              </div>
              <div>
                <div class="dialog-title">Account Tiers</div>
                <div class="dialog-subtitle">
                  {{ selectedAccount?.accountName }}
                </div>
              </div>
            </div>
            <v-btn v-if="permissions.add" class="btn-save" size="small" @click="openTierDialog()">
              <v-icon start>mdi-plus</v-icon>
              Add Tier
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <v-data-table :headers="tierHeaders" :items="accountTiers" :loading="loadingTiers" class="elevation-1" density="comfortable">
            <template v-slot:item.lowerAmount="{ item }">
              {{ formatCurrency(item.lowerAmount) }}
            </template>
            <template v-slot:item.upperAmount="{ item }">
              {{ formatCurrency(item.upperAmount) }}
            </template>
            <template v-slot:item.interestRate="{ item }"> {{ (item.interestRate * 100).toFixed(4) }}% </template>
            <template v-slot:item.actions="{ item }">
              <div class="action-buttons-group">
                <v-btn size="small" variant="outlined" color="primary" @click="openTierDialog(item)" class="action-btn">
                  <v-icon v-if="!permissions.edit" size="16" color="primary">mdi-eye-outline</v-icon>
                  <v-img v-else :src="images.EditImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">{{ permissions.edit ? 'Edit' : 'View' }}</v-tooltip>
                </v-btn>
                <v-btn v-if="permissions.delete" size="small" variant="outlined" color="error" @click="confirmDeleteTier(item)" class="action-btn">
                  <v-img :src="images.DeleteImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="closeTiersDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tier Create/Edit Dialog -->
    <v-dialog v-model="tierDialog" max-width="500px">
      <v-card class="add-proactive-dialog">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="d-flex align-center">
            <div class="header-icon-container">
              <v-avatar class="icon-background" size="46">
                <v-icon color="#4b2667" size="24">
                  {{ tierEditMode ? 'mdi-pencil' : 'mdi-plus' }}
                </v-icon>
              </v-avatar>
            </div>
            <div>
              <div class="dialog-title">
                {{ tierEditMode ? (permissions.edit ? 'Edit Tier' : 'View Tier') : 'Create Tier' }}
              </div>
              <div class="dialog-subtitle">
                {{ tierEditMode ? 'Update tier information' : 'Add new tier information' }}
              </div>
            </div>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="tierForm" v-model="tierFormValid">
            <div class="form-section">
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="tierFormData.tierName" label="Tier Name" variant="outlined" density="comfortable" hide-details="auto" />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="tierFormData.lowerAmount"
                    label="Lower Amount*"
                    variant="outlined"
                    density="comfortable"
                    type="number"
                    step="0.01"
                    :rules="[rules.required, rules.minZero]"
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="tierFormData.upperAmount"
                    label="Upper Amount*"
                    variant="outlined"
                    density="comfortable"
                    type="number"
                    step="0.01"
                    :rules="[rules.required, rules.minZero, rules.upperGreaterThanOrEqualLower]"
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model.number="tierFormData.interestRate"
                    label="Interest Rate* (decimal: 0.015 = 1.5%)"
                    variant="outlined"
                    density="comfortable"
                    type="number"
                    step="0.0001"
                    :rules="[rules.required, rules.interestRate]"
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model.number="tierFormData.minDays" label="Min Days" variant="outlined" density="comfortable" type="number" hide-details="auto" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model.number="tierFormData.maxDays" label="Max Days" variant="outlined" density="comfortable" type="number" hide-details="auto" />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="closeTierDialog">Cancel</v-btn>
          <v-btn v-if="tierEditMode ? permissions.edit : permissions.add" class="btn-save" :loading="savingTier" :disabled="!tierFormValid" @click="saveTier">
            {{ tierEditMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Categories Management Dialog -->
    <v-dialog v-model="categoriesDialog" max-width="1200px">
      <v-card class="add-proactive-dialog">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="d-flex align-center justify-space-between w-100">
            <div class="d-flex align-center">
              <div class="header-icon-container">
                <v-avatar class="icon-background" size="46">
                  <v-icon color="#4b2667" size="24">mdi-shape</v-icon>
                </v-avatar>
              </div>
              <div>
                <div class="dialog-title">Category Management</div>
                <div class="dialog-subtitle">Manage account categories and subcategories</div>
              </div>
            </div>
            <v-btn class="btn-save" size="small" @click="openCategoryDialog()">
              <v-icon start>mdi-plus</v-icon>
              Add Category
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-data-table :headers="categoryHeaders" :items="accountCategories" :loading="loadingCategories" class="elevation-1" density="comfortable" :items-per-page="10">
            <template v-slot:item.activeStatus="{ item }">
              <v-chip :color="item.activeStatus ? 'success' : 'error'" size="small" variant="outlined">
                {{ item.activeStatus ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>

            <template v-slot:item.subCategoriesCount="{ item }">
              <v-chip color="info" size="small" variant="outlined"> {{ item.subCategoriesCount || 0 }} Subcategories </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="action-buttons-group">
                <v-btn size="small" variant="outlined" color="secondary" @click="viewSubCategories(item)" class="action-btn">
                  <v-icon size="16">mdi-view-list</v-icon>
                  <v-tooltip activator="parent" location="top">View Subcategories</v-tooltip>
                </v-btn>
                <v-btn size="small" variant="outlined" color="primary" @click="openCategoryDialog(item)" class="action-btn">
                  <v-icon v-if="!permissions.edit" size="16" color="primary">mdi-eye-outline</v-icon>
                  <v-img v-else :src="images.EditImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">{{ permissions.edit ? 'Edit' : 'View' }}</v-tooltip>
                </v-btn>
                <v-btn v-if="permissions.delete" size="small" variant="outlined" color="error" @click="confirmDeleteCategory(item)" class="action-btn">
                  <v-img :src="images.DeleteImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="closeCategoriesDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Category Create/Edit Dialog -->
    <v-dialog v-model="categoryDialog" max-width="800px">
      <v-card class="add-proactive-dialog">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="d-flex align-center">
            <div class="header-icon-container">
              <v-avatar class="icon-background" size="46">
                <v-icon color="#4b2667" size="24">
                  {{ categoryEditMode ? 'mdi-pencil' : 'mdi-plus' }}
                </v-icon>
              </v-avatar>
            </div>
            <div>
              <div class="dialog-title">
                {{ categoryEditMode ? (permissions.edit ? 'Edit Category' : 'View Category') : 'Create Category' }}
              </div>
              <div class="dialog-subtitle">
                {{ categoryEditMode ? 'Update category information' : 'Add new category with subcategories' }}
              </div>
            </div>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="categoryForm" v-model="categoryFormValid">
            <div class="form-section">
              <h3 class="section-title">Category Information</h3>
              <v-row>
                <v-col cols="6">
                  <v-text-field v-model="categoryFormData.categoryCode" label="Category Code*" variant="outlined" density="comfortable" :rules="[rules.required]" hide-details="auto" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="categoryFormData.categoryName" label="Category Name*" variant="outlined" density="comfortable" :rules="[rules.required]" hide-details="auto" />
                </v-col>
                <v-col cols="12">
                  <v-textarea v-model="categoryFormData.description" label="Description" variant="outlined" density="comfortable" rows="3" hide-details="auto" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model.number="categoryFormData.displayOrder" label="Display Order" variant="outlined" density="comfortable" type="number" hide-details="auto" />
                </v-col>
                <v-col cols="6">
                  <v-checkbox v-model="categoryFormData.activeStatus" label="Active" color="primary" hide-details />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="closeCategoryDialog">Cancel</v-btn>
          <v-btn v-if="categoryEditMode ? permissions.edit : permissions.add" class="btn-save" :loading="savingCategory" :disabled="!categoryFormValid" @click="saveCategory">
            {{ categoryEditMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Subcategories View Dialog -->
    <v-dialog v-model="subCategoriesDialog" max-width="1000px">
      <v-card class="add-proactive-dialog">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="d-flex align-center justify-space-between w-100">
            <div class="d-flex align-center">
              <div class="header-icon-container">
                <v-avatar class="icon-background" size="46">
                  <v-icon color="#4b2667" size="24">mdi-view-list</v-icon>
                </v-avatar>
              </div>
              <div>
                <div class="dialog-title">Subcategories</div>
                <div class="dialog-subtitle">
                  {{ selectedCategory?.categoryName }}
                </div>
              </div>
            </div>
            <v-btn class="btn-save" size="small" @click="openSubCategoryDialog()" width="180">
              <v-icon start>mdi-plus</v-icon>
              Add Subcategory
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <v-data-table :headers="subCategoryHeaders" :items="categorySubCategories" :loading="loadingSubCategories" class="elevation-1" density="comfortable">
            <template v-slot:item.activeStatus="{ item }">
              <v-chip :color="item.activeStatus ? 'success' : 'error'" size="small" variant="outlined">
                {{ item.activeStatus ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="action-buttons-group">
                <v-btn size="small" variant="outlined" color="primary" @click="openSubCategoryDialog(item)" class="action-btn">
                  <v-icon v-if="!permissions.edit" size="16" color="primary">mdi-eye-outline</v-icon>
                  <v-img v-else :src="images.EditImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">{{ permissions.edit ? 'Edit' : 'View' }}</v-tooltip>
                </v-btn>
                <v-btn v-if="permissions.delete" size="small" variant="outlined" color="error" @click="confirmDeleteSubCategory(item)" class="action-btn">
                  <v-img :src="images.DeleteImg" width="14" height="14" />
                  <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="closeSubCategoriesDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Subcategory Create/Edit Dialog -->
    <v-dialog v-model="subCategoryDialog" max-width="500px">
      <v-card class="add-proactive-dialog">
        <v-card-title class="dialog-header dialog-header-add">
          <div class="d-flex align-center">
            <div class="header-icon-container">
              <v-avatar class="icon-background" size="46">
                <v-icon color="#4b2667" size="24">
                  {{ subCategoryEditMode ? 'mdi-pencil' : 'mdi-plus' }}
                </v-icon>
              </v-avatar>
            </div>
            <div>
              <div class="dialog-title">
                {{ subCategoryEditMode ? (permissions.edit ? 'Edit Subcategory' : 'View Subcategory') : 'Create Subcategory' }}
              </div>
              <div class="dialog-subtitle">
                {{ subCategoryEditMode ? 'Update subcategory information' : 'Add new subcategory' }}
              </div>
            </div>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content">
          <v-form ref="subCategoryForm" v-model="subCategoryFormValid">
            <div class="form-section">
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="subCategoryFormData.subCategoryCode" label="Subcategory Code*" variant="outlined" density="comfortable" :rules="[rules.required]" hide-details="auto" />
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="subCategoryFormData.subCategoryName" label="Subcategory Name*" variant="outlined" density="comfortable" :rules="[rules.required]" hide-details="auto" />
                </v-col>
                <v-col cols="12">
                  <v-textarea v-model="subCategoryFormData.description" label="Description" variant="outlined" density="comfortable" rows="3" hide-details="auto" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model.number="subCategoryFormData.displayOrder" label="Display Order" variant="outlined" density="comfortable" type="number" hide-details="auto" />
                </v-col>
                <v-col cols="6">
                  <v-checkbox v-model="subCategoryFormData.activeStatus" label="Active" color="primary" hide-details />
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="closeSubCategoryDialog">Cancel</v-btn>
          <v-btn v-if="subCategoryEditMode ? permissions.edit : permissions.add" class="btn-save" :loading="savingSubCategory" :disabled="!subCategoryFormValid" @click="saveSubCategory">
            {{ subCategoryEditMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Dialog -->
    <v-dialog v-model="loadingDialog" persistent width="300">
      <v-card color="primary">
        <v-card-text class="text-white text-center pa-6">
          <v-progress-circular indeterminate color="white" class="mb-4" />
          <div>Loading Please Wait...</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import editImg from '@/assets/edit1.png';
import deleteImg from '@/assets/delete1.png';
import thaiIcon from '@/assets/images/thai-flag.svg';
import engIcon from '@/assets/images/eng-flag.svg';
import chaineseIcon from '@/assets/images/chainese-flag.svg';
import excelIcon from '@/assets/images/excel-icon.svg';

export default {
  name: 'AccountInfoManagement',
  inject: ['flagNav', 'FontColorTitle'],
  data() {
    return {
      today: null,
      loading: false,
      loadingDialog: false,
      dialog: false,
      tiersDialog: false,
      tierDialog: false,
      categoriesDialog: false,
      categoryDialog: false,
      subCategoriesDialog: false,
      subCategoryDialog: false,
      duplicateDialog: false,
      editMode: false,
      tierEditMode: false,
      categoryEditMode: false,
      subCategoryEditMode: false,
      saving: false,
      savingTier: false,
      savingCategory: false,
      savingSubCategory: false,
      savingDuplicate: false,
      loadingTiers: false,
      loadingCategories: false,
      loadingSubCategories: false,
      formValid: false,
      tierFormValid: false,
      categoryFormValid: false,
      subCategoryFormValid: false,
      duplicateFormValid: false,
      startDateMenu: false,
      endDateMenu: false,
      search: '',
      categoryFilter: null,
      subCategoryFilter: null,
      customerScopeFilter: null,

      accountInfos: [],
      accountTiers: [],
      accountCategories: [],
      categorySubCategories: [],
      allSubCategories: [], // All subcategories for lookup
      formSubCategories: [],
      formCalculationMethods: [
        {
          title: 'Tier Rate',
          value: 'TierRate',
        },
        {
          title: 'Split Rate',
          value: 'SplitRate',
        },
        {
          title: 'Fixed Rate',
          value: 'FixedRate',
        },
      ],
      selectedAccount: null,
      selectedCategory: null,

      images: {
        EditImg: editImg,
        DeleteImg: deleteImg,
        ExcelImg: excelIcon,
        ThaiIcon: thaiIcon,
        EngIcon: engIcon,
        ChaineseIcon: chaineseIcon,
      },

      formData: {
        accountInfoID: 0,
        accountCode: '',
        accountName: '',
        categoryID: null,
        subCategoryID: null,
        calculationMethod: null,
        description: '',
        effectiveStartDate: null,
        effectiveEndDate: null,
        isNewUser: false,
        isActive: true,
      },
      // NEW: Duplicate form data
        duplicateFormData: {
          accountName: '',
          originalAccountCode: '',
          originalAccountName: '',
          accountCode: '',
          categoryID: null,
          subCategoryID: null,
          calculationMethod: null,
          description: '',
          effectiveStartDate: null,
          effectiveEndDate: null,
          isNewUser: false,
          isActive: true,
          originalAccountInfoID: null,
          tiersCount: 0,
          duplicateTiers: true,
          originalTiers: [],
        },
      tempTiersForDuplication: [],
      tierFormData: {
        accountTierID: 0,
        accountInfoID: 0,
        tierName: '',
        lowerAmount: 0,
        upperAmount: 0,
        interestRate: 0,
        minDays: null,
        maxDays: null,
      },

      categoryFormData: {
        categoryID: 0,
        categoryCode: '',
        categoryName: '',
        description: '',
        displayOrder: 0,
        isActive: true,
        activeStatus: true,
        subCategories: [],
      },

      subCategoryFormData: {
        subCategoryID: 0,
        categoryID: 0,
        subCategoryCode: '',
        subCategoryName: '',
        description: '',
        displayOrder: 0,
        isActive: true,
        activeStatus: true,
      },

      rules: {
        required: (value) => !!value || value === 0 || 'Required.',
        minZero: (value) => value >= 0 || value === 0 || 'Must be greater than or equal to 0',
        interestRate: (value) => (value >= 0 && value <= 1) || 'Interest rate must be between 0 and 1',
        upperGreaterThanOrEqualLower: (value) => {
          return value >= this.tierFormData.lowerAmount || 'Upper amount must be greater than or equal to lower amount';
        },
      },

      headers: [
        { title: 'Account Code', key: 'accountCode', sortable: true },
        { title: 'Account Name', key: 'accountName', sortable: true },
        { title: 'Category / Subcategory', key: 'categoryID', sortable: true },
        { title: 'Description', key: 'description', sortable: false },
        { title: 'Effective Period', key: 'effectivePeriod', sortable: false },
        { title: 'Status', key: 'isActive', sortable: true, class: 'sticky-status' },
        { title: null, key: 'duplicate', sortable: false, class: 'sticky-duplicate', width: '100px' }, // NEW
        { title: 'Actions', key: 'actions', sortable: false, class: 'sticky-action' },
      ],

      tierHeaders: [
        { title: 'Tier Name', key: 'tierName', sortable: true },
        { title: 'Lower Amount', key: 'lowerAmount', sortable: true },
        { title: 'Upper Amount', key: 'upperAmount', sortable: true },
        { title: 'Interest Rate', key: 'interestRate', sortable: true },
        { title: 'Min Days', key: 'minDays', sortable: true },
        { title: 'Max Days', key: 'maxDays', sortable: true },
        { title: 'Actions', key: 'actions', sortable: false, class: 'sticky-action' },
      ],

      categoryHeaders: [
        { title: 'Category Code', key: 'categoryCode', sortable: true },
        { title: 'Category Name', key: 'categoryName', sortable: true },
        { title: 'Description', key: 'description', sortable: false },
        { title: 'Display Order', key: 'displayOrder', sortable: true },
        { title: 'Subcategories', key: 'subCategoriesCount', sortable: true },
        { title: 'Status', key: 'activeStatus', sortable: true },
        { title: 'Actions', key: 'actions', sortable: false, class: 'sticky-action' },
      ],

      subCategoryHeaders: [
        { title: 'Subcategory Code', key: 'subCategoryCode', sortable: true },
        { title: 'Subcategory Name', key: 'subCategoryName', sortable: true },
        { title: 'Description', key: 'description', sortable: false },
        { title: 'Display Order', key: 'displayOrder', sortable: true },
        { title: 'Status', key: 'activeStatus', sortable: true },
        { title: 'Actions', key: 'actions', sortable: false, class: 'sticky-action' },
      ],
    };
  },

  computed: {
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('AccountInfoManagement');
    },
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('AccountInfoManagement'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('AccountInfoManagement'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('AccountInfoManagement'));
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('AccountInfoManagement'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('AccountInfoManagement'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('AccountInfoManagement'),
      }
    },
    formCategories() {
      return this.accountCategories.map((cat) => ({
        title: cat.categoryName,
        value: cat.categoryID,
      }));
    },

    categories() {
      return [
        { title: 'All Categories', value: null },
        ...this.accountCategories.map((cat) => ({
          title: cat.categoryName,
          value: cat.categoryID,
        })),
      ];
    },

    subCategories() {
      return [
        { title: 'All Subcategories', value: null },
        ...this.allSubCategories.map((subCat) => ({
          title: subCat.subCategoryName,
          value: subCat.subCategoryID,
        })),
      ];
    },

    customerScopeOptions() {
      return [
        { title: this.accountInfoText('allScopes'), value: null },
        { title: this.accountInfoText('allCustomers'), value: false },
        { title: this.accountInfoText('newCustomersOnly'), value: true },
      ];
    },

    filteredItems() {
      let items = this.accountInfos;

      if (this.search) {
        items = items.filter(
          (item) =>
            item.accountCode.toLowerCase().includes(this.search.toLowerCase()) ||
            item.accountName.toLowerCase().includes(this.search.toLowerCase()) ||
            this.getCategoryNameById(item.categoryID).toLowerCase().includes(this.search.toLowerCase()) ||
            this.getSubCategoryNameById(item.subCategoryID).toLowerCase().includes(this.search.toLowerCase()) ||
            this.getCustomerScopeText(item.isNewUser).toLowerCase().includes(this.search.toLowerCase())
        );
      }

      if (this.categoryFilter) {
        items = items.filter((item) => item.categoryID === this.categoryFilter);
      }

      if (this.subCategoryFilter) {
        items = items.filter((item) => item.subCategoryID === this.subCategoryFilter);
      }

      if (this.customerScopeFilter !== null) {
        items = items.filter((item) => item.isNewUser === this.customerScopeFilter);
      }

      return items;
    },
  },

  async mounted() {
    this.GetDate();
    this.loadAccountInfos();
    this.loadAccountCategories();
    this.loadAllSubCategories();
  },

  methods: {
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1;
      let yyyy = today1.getFullYear();
      if (dd < 10) {
        dd = '0' + dd;
      }
      if (mm < 10) {
        mm = '0' + mm;
      }
      this.today = dd + '/' + mm + '/' + yyyy;
    },
    async loadAccountInfos() {
      this.loading = true;
      try {
        this.$call.GetAccountInfos(
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.accountInfos = dataResponse.data.map((item) => {
                const mappedItem = {
                  ...item,
                  categoryID: item.categoryID || item.CategoryID || null,
                  subCategoryID: item.subCategoryID || item.SubCategoryID || null,
                  isActive: item.isActive !== undefined ? item.isActive : item.IsActive,
                  effectiveStartDate: item.effectiveStartDate || item.EffectiveStartDate || null,
                  effectiveEndDate: item.effectiveEndDate || item.EffectiveEndDate || null,
                  isNewUser: item.isNewUser !== undefined ? item.isNewUser : item.IsNewUser || false,
                };

                if (item.categoryID !== mappedItem.categoryID) {
                  console.log(`Mapped category for ${item.accountName}: ${item.category} -> CategoryID: ${mappedItem.categoryID}`);
                }

                if (item.subCategoryID !== mappedItem.subCategoryID) {
                  console.log(`Mapped subcategory for ${item.accountName}: SubCategoryID: ${mappedItem.subCategoryID}`);
                }

                return mappedItem;
              });

              console.log('Loaded account infos with CategoryID:', this.accountInfos);
            } else {
              this.accountInfos = [];
            }
            this.loading = false;
          },
          (errorResource) => {
            this.loading = false;
            this.$swal.fire('Error', 'Failed to load account infos', 'error');
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.loading = false;
        this.$swal.fire('Error', 'Failed to load account infos', 'error');
        console.error(error);
      }
    },

    async loadAccountTiers(accountId) {
      this.loadingTiers = true;
      try {
        this.$call.GetAccountTiers(
          accountId,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.accountTiers = dataResponse.data;
            } else {
              this.accountTiers = [];
              this.$swal.fire('Error', dataResponse.message || 'Failed to load account tiers', 'error');
            }
            this.loadingTiers = false;
          },
          (errorResource) => {
            this.loadingTiers = false;
            this.$swal.fire('Error', 'Failed to load account tiers', 'error');
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.loadingTiers = false;
        this.$swal.fire('Error', 'Failed to load account tiers', 'error');
        console.error(error);
      }
    },

    openDialog(item = null) {
      this.editMode = !!item;
      if (item) {
        this.formData = { ...item, isNewUser: item.isNewUser === true };
        // Load subcategories when editing an item with a category
        if (item.categoryID) {
          this.loadFormSubCategories(item.categoryID);
        }
      } else {
        this.formData = {
          accountInfoID: 0,
          accountCode: '',
          accountName: '',
          categoryID: null,
          subCategoryID: null,
          description: '',
          effectiveStartDate: null,
          effectiveEndDate: null,
          isNewUser: false,
          isActive: true,
        };
        this.formSubCategories = [];
      }
      this.startDateMenu = false;
      this.endDateMenu = false;
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.startDateMenu = false;
      this.endDateMenu = false;
      this.formData = {
        accountInfoID: 0,
        accountCode: '',
        accountName: '',
        categoryID: null,
        subCategoryID: null,
        description: '',
        effectiveStartDate: null,
        effectiveEndDate: null,
        isNewUser: false,
        isActive: true,
      };
      this.formSubCategories = [];
      this.$refs.form?.resetValidation();
    },

    async saveAccountInfo() {
      if (!this.formValid) return;

      this.saving = true;
      try {
        if (!this.formData.categoryID) {
          this.$swal.fire('Validation Error', 'Please select a category', 'warning');
          this.saving = false;
          return;
        }
        const selectedCategory = this.accountCategories.find((cat) => cat.categoryID === this.formData.categoryID);
        if (!selectedCategory) {
          this.$swal.fire('Validation Error', 'Selected category is not valid. Please select a valid category.', 'warning');
          this.saving = false;
          return;
        }
        this.formData.effectiveStartDate = this.formatDateForAPI(this.formData.effectiveStartDate);
        this.formData.effectiveEndDate = this.formatDateForAPI(this.formData.effectiveEndDate);
        if (this.editMode) {
          this.$call.UpdateAccountInfo(
            this.formData.accountInfoID,
            this.formData,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.$swal.fire('Success', 'Account info updated successfully', 'success');
                this.closeDialog();
                this.loadAccountInfos();
                this.loadAccountCategories();
              } else {
                this.$swal.fire('Error', dataResponse.message || 'Failed to update account info', 'error');
              }
              this.saving = false;
            },
            (errorResource) => {
              this.saving = false;
              this.$swal.fire('Error', 'Failed to update account info', 'error');
              console.error('Update account info error:', errorResource);
            }
          );
        } else {
          this.$call.CreateAccountInfo(
            this.formData,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.$swal.fire('Success', 'Account info created successfully', 'success');
                this.closeDialog();
                this.loadAccountInfos();
                this.loadAccountCategories();
              } else {
                this.$swal.fire('Error', dataResponse.message || 'Failed to create account info', 'error');
              }
              this.saving = false;
            },
            (errorResource) => {
              this.saving = false;
              this.$swal.fire('Error', 'Failed to create account info', 'error');
              console.error('Create account info error:', errorResource);
            }
          );
        }
      } catch (error) {
        this.saving = false;
        this.$swal.fire('Error', 'Failed to save account info', 'error');
        console.error('Save account info exception:', error);
      }
    },
    // Updated openDuplicateDialog method
    async openDuplicateDialog(item) {
      // Clear any previous data first
      this.tempTiersForDuplication = [];

      this.duplicateDialog = true;

      // Load tiers for the original account first
      await this.loadTiersForDuplication(item.accountInfoID);

      // Generate a unique account code with timestamp
      const timestamp = Date.now();
      const uniqueCode = `${item.accountCode}_${timestamp}`;

      this.duplicateFormData = {
        accountName: `${item.accountName} (Copy)`,
        originalAccountCode: item.accountCode,
        originalAccountName: item.accountName,
        accountCode: uniqueCode, // Use timestamp-based unique code
        categoryID: item.categoryID,
        subCategoryID: item.subCategoryID,
        calculationMethod: item.calculationMethod,
        description: item.description,
        effectiveStartDate: item.effectiveStartDate,
        effectiveEndDate: item.effectiveEndDate,
        isNewUser: item.isNewUser === true,
        isActive: true,
        originalAccountInfoID: item.accountInfoID,
        tiersCount: this.tempTiersForDuplication?.length || 0,
        duplicateTiers: true,
        originalTiers: this.tempTiersForDuplication ? [...this.tempTiersForDuplication] : [],
      };
    },

    // Updated loadTiersForDuplication method
    async loadTiersForDuplication(accountId) {
      // Reset the temp array before loading
      this.tempTiersForDuplication = [];

      return new Promise((resolve, reject) => {
        try {
          this.$call.GetAccountTiers(
            accountId,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.tempTiersForDuplication = dataResponse.data || [];
                console.log('Loaded tiers for duplication:', this.tempTiersForDuplication.length);
              } else {
                this.tempTiersForDuplication = [];
                console.log('No tiers found or error in response');
              }
              resolve();
            },
            (errorResource) => {
              console.error('Error loading tiers for duplication:', errorResource);
              this.tempTiersForDuplication = [];
              resolve(); // Still resolve to continue with duplication
            }
          );
        } catch (error) {
          console.error('Exception in loadTiersForDuplication:', error);
          this.tempTiersForDuplication = [];
          resolve();
        }
      });
    },

    // Updated closeDuplicateDialog method
    closeDuplicateDialog() {
      this.duplicateDialog = false;

      // Clear temp tiers first
      this.tempTiersForDuplication = [];

      this.duplicateFormData = {
        accountName: '',
        originalAccountCode: '',
        originalAccountName: '',
        accountCode: '',
        categoryID: null,
        subCategoryID: null,
        calculationMethod: null,
        description: '',
        effectiveStartDate: null,
        effectiveEndDate: null,
        isNewUser: false,
        isActive: true,
        originalAccountInfoID: null,
        tiersCount: 0,
        duplicateTiers: true,
        originalTiers: [],
      };

      this.$refs.duplicateForm?.resetValidation();
    },

    // Updated saveDuplicate method with better error handling
    async saveDuplicate() {
      if (!this.duplicateFormValid) return;

      this.savingDuplicate = true;
      try {
        // Generate a new unique code with timestamp to ensure uniqueness
        const timestamp = Date.now();
        const uniqueCode = `${this.duplicateFormData.originalAccountCode}_COPY_${timestamp}`;

        // Prepare payload for duplication
        const payload = {
          accountCode: uniqueCode,
          accountName: this.duplicateFormData.accountName,
          categoryID: this.duplicateFormData.categoryID,
          subCategoryID: this.duplicateFormData.subCategoryID,
          calculationMethod: this.duplicateFormData.calculationMethod,
          description: this.duplicateFormData.description,
          effectiveStartDate: this.duplicateFormData.effectiveStartDate,
          effectiveEndDate: this.duplicateFormData.effectiveEndDate,
          isNewUser: this.duplicateFormData.isNewUser === true,
          isActive: this.duplicateFormData.isActive,
        };

        // Validation: Check if category is selected
        if (!payload.categoryID) {
          this.$swal.fire('Validation Error', 'Please select a category', 'warning');
          this.savingDuplicate = false;
          return;
        }

        const selectedCategory = this.accountCategories.find((cat) => cat.categoryID === payload.categoryID);
        if (!selectedCategory) {
          this.$swal.fire('Validation Error', 'Selected category is not valid. Please select a valid category.', 'warning');
          this.savingDuplicate = false;
          return;
        }

        // Store original tiers before creating account (in case it gets cleared)
        const originalTiersToClone = this.duplicateFormData.originalTiers ? [...this.duplicateFormData.originalTiers] : [];
        const shouldDuplicateTiers = this.duplicateFormData.duplicateTiers;

        // Create the duplicated account using the same API as create
        this.$call.CreateAccountInfo(
          payload,
          async (dataResponse) => {
            if (dataResponse.status === 0) {
              const newAccountInfoID = dataResponse.data.accountInfoID || dataResponse.data.AccountInfoID;

              // If user wants to duplicate tiers and there are tiers to duplicate
              if (shouldDuplicateTiers && originalTiersToClone.length > 0 && newAccountInfoID) {
                await this.duplicateTiers(newAccountInfoID, originalTiersToClone);
              }

              this.$swal.fire({
                icon: 'success',
                title: 'Success!',
                text: `Account info duplicated successfully${shouldDuplicateTiers && originalTiersToClone.length > 0 ? ' with ' + originalTiersToClone.length + ' tier(s)' : ''}`,
                timer: 2500,
                showConfirmButton: false,
              });

              this.closeDuplicateDialog();
              this.loadAccountInfos();
              this.loadAccountCategories();
            } else {
              console.error('Create account error response:', dataResponse);
              this.$swal.fire('Error', dataResponse.message || 'Failed to duplicate account info', 'error');
            }
            this.savingDuplicate = false;
          },
          (errorResource) => {
            this.savingDuplicate = false;
            console.error('Duplicate account info error:', errorResource);
            
            // Extract detailed error message
            let errorMessage = 'Failed to duplicate account info';
            if (errorResource && errorResource.response) {
              console.error('Error response:', errorResource.response);
              if (errorResource.response.data) {
                console.error('Error data:', errorResource.response.data);
                if (errorResource.response.data.message) {
                  errorMessage = errorResource.response.data.message;
                } else if (errorResource.response.data.errors) {
                  // Handle validation errors
                  const errors = errorResource.response.data.errors;
                  errorMessage = Object.keys(errors).map(key => `${key}: ${errors[key].join(', ')}`).join('\n');
                }
              }
              errorMessage += ` (Status: ${errorResource.response.status})`;
            } else if (errorResource && errorResource.message) {
              errorMessage = errorResource.message;
            }
            
            this.$swal.fire('Error', errorMessage, 'error');
          }
        );
      } catch (error) {
        this.savingDuplicate = false;
        console.error('Duplicate account info exception:', error);
        this.$swal.fire('Error', 'Failed to duplicate account info: ' + (error.message || 'Unknown error'), 'error');
      }
    },

    // Updated duplicateTiers method with better error handling
    async duplicateTiers(newAccountInfoID, originalTiers) {
      if (!originalTiers || originalTiers.length === 0) {
        return;
      }

      const tierPromises = originalTiers.map((tier, index) => {
        return new Promise((resolve, reject) => {
          const tierPayload = {
            accountInfoID: newAccountInfoID,
            tierName: tier.tierName,
            lowerAmount: tier.lowerAmount || 0,
            upperAmount: tier.upperAmount || 0,
            interestRate: tier.interestRate || 0,
            minDays: tier.minDays == "" ? null : tier.minDays,
            maxDays: tier.maxDays == "" ? null : tier.maxDays,
          };

          this.$call.CreateAccountTier(
            newAccountInfoID,
            tierPayload,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                resolve(true);
              } else {
                console.error(`Failed to duplicate tier "${tier.tierName}":`, dataResponse.message);
                resolve(false);
              }
            },
            (errorResource) => {
              console.error(`Error duplicating tier "${tier.tierName}":`, errorResource);
              resolve(false);
            }
          );
        });
      });

      try {
        const results = await Promise.all(tierPromises);
        const successCount = results.filter(r => r === true).length;
      } catch (error) {
        console.error('Error in tier duplication process:', error);
      }
    },
    async confirmDelete(item) {
      const confirmed = await this.$swal.fire({
        title: 'Are you sure?',
        text: `Delete account info: ${item.accountName}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      });

      if (confirmed.isConfirmed) {
        await this.deleteAccountInfo(item.accountInfoID);
      }
    },

    async deleteAccountInfo(id) {
      this.loadingDialog = true;
      try {
        this.$call.DeleteAccountInfo(
          id,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.$swal.fire('Success', 'Account info deleted successfully', 'success');
              this.loadAccountInfos();
            } else {
              this.$swal.fire('Error', dataResponse.message || 'Failed to delete account info', 'error');
            }
            this.loadingDialog = false;
          },
          (errorResource) => {
            this.loadingDialog = false;
            this.$swal.fire('Error', 'Failed to delete account info', 'error');
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.loadingDialog = false;
        this.$swal.fire('Error', 'Failed to delete account info', 'error');
        console.error(error);
      }
    },

    async viewTiers(item) {
      this.selectedAccount = item;
      this.tiersDialog = true;
      this.loadAccountTiers(item.accountInfoID);
    },

    closeTiersDialog() {
      this.tiersDialog = false;
      this.selectedAccount = null;
      this.accountTiers = [];
    },

    openTierDialog(item = null) {
      this.tierEditMode = !!item;
      if (item) {
        this.tierFormData = { ...item };
      } else {
        this.tierFormData = {
          accountTierID: 0,
          accountInfoID: this.selectedAccount.accountInfoID,
          tierName: '',
          lowerAmount: 0,
          upperAmount: 0,
          interestRate: 0,
          minDays: null,
          maxDays: null,
        };
      }
      this.tierDialog = true;
    },

    closeTierDialog() {
      this.tierDialog = false;
      this.tierFormData = {};
      this.$refs.tierForm?.resetValidation();
    },

    async saveTier() {
      if (!this.tierFormValid) return;

      this.savingTier = true;
      try {
        this.tierFormData.minDays = this.tierFormData.minDays == "" ? null : this.tierFormData.minDays;
        this.tierFormData.maxDays = this.tierFormData.maxDays == "" ? null : this.tierFormData.maxDays;
        if (this.tierEditMode) {
          this.$call.UpdateAccountTier(
            this.tierFormData.accountTierID,
            this.tierFormData,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.$swal.fire('Success', 'Tier updated successfully', 'success');
                this.closeTierDialog();
                this.loadAccountTiers(this.selectedAccount.accountInfoID);
              } else {
                this.$swal.fire('Error', dataResponse.message || 'Failed to update tier', 'error');
              }
              this.savingTier = false;
            },
            (errorResource) => {
              this.savingTier = false;
              this.$swal.fire('Error', 'Failed to update tier', 'error');
              console.error(errorResource);
            }
          );
        } else {
          this.$call.CreateAccountTier(
            this.selectedAccount.accountInfoID,
            this.tierFormData,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.$swal.fire('Success', 'Tier created successfully', 'success');
                this.closeTierDialog();
                this.loadAccountTiers(this.selectedAccount.accountInfoID);
              } else {
                this.$swal.fire('Error', dataResponse.message || 'Failed to create tier', 'error');
              }
              this.savingTier = false;
            },
            (errorResource) => {
              this.savingTier = false;
              this.$swal.fire('Error', 'Failed to create tier', 'error');
              console.error(errorResource);
            }
          );
        }
      } catch (error) {
        this.savingTier = false;
        this.$swal.fire('Error', 'Failed to save tier', 'error');
        console.error(error);
      }
    },

    async confirmDeleteTier(item) {
      const confirmed = await this.$swal.fire({
        title: 'Are you sure?',
        text: `Delete tier: ${item.tierName || 'Unnamed tier'}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      });

      if (confirmed.isConfirmed) {
        await this.deleteTier(item.accountTierID);
      }
    },

    async deleteTier(tierId) {
      this.loadingDialog = true;
      try {
        this.$call.DeleteAccountTier(
          tierId,
          (dataResponse) => {
            if (dataResponse.status === 0) {
              this.$swal.fire('Success', 'Tier deleted successfully', 'success');
              this.loadAccountTiers(this.selectedAccount.accountInfoID);
            } else {
              this.$swal.fire('Error', dataResponse.message || 'Failed to delete tier', 'error');
            }
            this.loadingDialog = false;
          },
          (errorResource) => {
            this.loadingDialog = false;
            this.$swal.fire('Error', 'Failed to delete tier', 'error');
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.loadingDialog = false;
        this.$swal.fire('Error', 'Failed to delete tier', 'error');
        console.error(error);
      }
    },

    getCategoryColor(category) {
      const colors = {
        saving: 'primary',
        fixed: 'success',
        tax: 'warning',
      };
      return colors[category] || 'grey';
    },

    getCategoryText(category) {
      const texts = {
        saving: 'Saving',
        fixed: 'Fixed Deposit',
        tax: 'Tax Free',
      };
      return texts[category] || category;
    },

    accountInfoText(key) {
      const locale = this.$i18n?.locale || localStorage.getItem('locale') || 'en';
      const text = {
        th: {
          customerScope: 'กลุ่มลูกค้า',
          allScopes: 'ทุกกลุ่มลูกค้า',
          allCustomers: 'ลูกค้าทุกคน',
          newCustomersOnly: 'ลูกค้าใหม่เท่านั้น',
          allCustomersTitle: 'ใช้กับลูกค้าทุกคน',
          newCustomersTitle: 'ใช้กับลูกค้าใหม่เท่านั้น',
          allCustomersDescription: 'Account info นี้ใช้ได้กับลูกค้าทุกคน โดยไม่จำกัดว่าเป็นลูกค้าใหม่',
          newCustomersDescription: 'Account info นี้ใช้เฉพาะเงื่อนไขสำหรับลูกค้าใหม่',
        },
        en: {
          customerScope: 'Customer Scope',
          allScopes: 'All customer scopes',
          allCustomers: 'All customers',
          newCustomersOnly: 'New customers only',
          allCustomersTitle: 'Available for all customers',
          newCustomersTitle: 'Available for new customers only',
          allCustomersDescription: 'This account info applies to all customers, regardless of new-customer status.',
          newCustomersDescription: 'This account info applies only to new-customer conditions.',
        },
      };

      return (text[locale] && text[locale][key]) || text.en[key] || key;
    },

    getCustomerScopeText(isNewUser) {
      return isNewUser ? this.accountInfoText('newCustomersOnly') : this.accountInfoText('allCustomers');
    },

    getCustomerScopeToggleTitle(isNewUser) {
      return isNewUser ? this.accountInfoText('newCustomersTitle') : this.accountInfoText('allCustomersTitle');
    },

    getCustomerScopeDescription(isNewUser) {
      return isNewUser ? this.accountInfoText('newCustomersDescription') : this.accountInfoText('allCustomersDescription');
    },

    getCustomerScopeColor(item) {
      return item.categoryID ? this.getCategoryColorById(item.categoryID) : 'grey';
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('th-TH', {
        style: 'currency',
        currency: 'THB',
        minimumFractionDigits: 2,
      }).format(amount);
    },

    // Date helper methods
    formatDateDisplay(date) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    },

    formatEffectivePeriod(startDate, endDate) {
      const start = this.formatDateDisplay(startDate);
      const end = this.formatDateDisplay(endDate);
      if (!start && !end) return '-';
      if (start && !end) return `${start} - `;
      if (!start && end) return ` - ${end}`;
      return `${start} - ${end}`;
    },

    onStartDateChange(value) {
      this.startDateMenu = false;
      // Validation: clear end date if it's before start date
      if (this.formData.effectiveEndDate && this.formData.effectiveStartDate) {
        if (new Date(this.formData.effectiveStartDate) > new Date(this.formData.effectiveEndDate)) {
          this.formData.effectiveEndDate = null;
        }
      }
    },

    onEndDateChange(value) {
      this.endDateMenu = false;
    },

    // Category helper methods
    getCategoryNameById(categoryId) {
      if (!categoryId) return 'No Category';
      const category = this.accountCategories.find((cat) => cat.categoryID === categoryId);
      return category ? category.categoryName : `...`;
    },

    getCategoryColorById(categoryId) {
      if (!categoryId) return 'grey';
      const category = this.accountCategories.find((cat) => cat.categoryID === categoryId);
      if (!category) return 'grey';

      const colorMap = {
        saving: 'primary',
        fixed: 'success',
        tax: 'warning',
      };
      return colorMap[category.categoryCode] || 'info';
    },

    // Subcategory helper methods
    getSubCategoryNameById(subCategoryId) {
      if (!subCategoryId) return 'No Subcategory';
      // First try to find in all subcategories array
      let subCategory = this.allSubCategories.find((subCat) => subCat.subCategoryID === subCategoryId);
      if (!subCategory) {
        // Fallback to category subcategories array
        subCategory = this.categorySubCategories.find((subCat) => subCat.subCategoryID === subCategoryId);
      }
      return subCategory ? subCategory.subCategoryName : ``;
    },

    getSubCategoryColorById(subCategoryId) {
      if (!subCategoryId) return 'grey';
      // First try to find in all subcategories array
      let subCategory = this.allSubCategories.find((subCat) => subCat.subCategoryID === subCategoryId);
      if (!subCategory) {
        // Fallback to category subcategories array
        subCategory = this.categorySubCategories.find((subCat) => subCat.subCategoryID === subCategoryId);
      }
      if (!subCategory) return 'grey';
      return 'secondary';
    },

    // Category change handler
    onCategoryChange(categoryId) {
      this.formData.subCategoryID = null; // Reset subcategory when category changes
      if (categoryId) {
        this.loadFormSubCategories(categoryId);
      } else {
        this.formSubCategories = [];
      }
    },

    async loadAllSubCategories() {
      try {
        if (this.$call.GetAllSubCategories && typeof this.$call.GetAllSubCategories === 'function') {
          this.$call.GetAllSubCategories(
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.allSubCategories = dataResponse.data || [];
                // Also populate categorySubCategories for backward compatibility
                this.categorySubCategories = dataResponse.data || [];
                console.log('Loaded all subcategories:', this.allSubCategories);
              } else {
                this.allSubCategories = [];
                this.categorySubCategories = [];
              }
            },
            (errorResource) => {
              console.log('Subcategory API not available yet, using default subcategories');
              this.allSubCategories = [];
              this.categorySubCategories = [];
            }
          );
        } else {
          console.log('GetAllSubCategories method not available');
          this.allSubCategories = [];
          this.categorySubCategories = [];
        }
      } catch (error) {
        console.error('Error loading subcategories:', error);
        this.allSubCategories = [];
        this.categorySubCategories = [];
      }
    },
    // Load subcategories for form dropdown
    async loadFormSubCategories(categoryId) {
      this.loadingSubCategories = true;
      try {
        if (this.$call.GetSubCategoriesByCategory && typeof this.$call.GetSubCategoriesByCategory === 'function') {
          this.$call.GetSubCategoriesByCategory(
            categoryId,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.formSubCategories = dataResponse.data.map((subCat) => ({
                  title: subCat.subCategoryName,
                  value: subCat.subCategoryID,
                }));
                console.log(`Loaded subcategories for category ${categoryId}:`, this.formSubCategories);
              } else {
                this.formSubCategories = [];
                console.log('No subcategories found for category:', categoryId);
              }
              this.loadingSubCategories = false;
            },
            (errorResource) => {
              console.log('GetSubCategoriesByCategory API not available:', errorResource);
              this.formSubCategories = [];
              this.loadingSubCategories = false;
            }
          );
        } else {
          console.log('GetSubCategoriesByCategory method not available');
          this.formSubCategories = [];
          this.loadingSubCategories = false;
        }
      } catch (error) {
        console.error('Error loading form subcategories:', error);
        this.formSubCategories = [];
        this.loadingSubCategories = false;
      }
    },

    // Category Management Methods
    async loadAccountCategories() {
      this.loadingCategories = true;
      try {
        if (this.$call.GetAccountCategories && typeof this.$call.GetAccountCategories === 'function') {
          this.$call.GetAccountCategories(
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.accountCategories = dataResponse.data || [];
              } else {
                this.loadDefaultCategories();
              }
              this.loadingCategories = false;
            },
            (errorResource) => {
              this.loadDefaultCategories();
              this.loadingCategories = false;
            }
          );
        } else {
          this.loadDefaultCategories();
        }
      } catch (error) {
        this.loadDefaultCategories();
      }
    },

    loadDefaultCategories() {
      this.accountCategories = [];

      // Populate categories dropdown
      this.categories = [
        { title: 'All Categories', value: '' },
        ...this.accountCategories.map((cat) => ({
          title: cat.categoryName,
          value: cat.categoryID,
        })),
      ];

      this.loadingCategories = false;
    },

    async loadSubCategoriesByCategory(categoryId) {
      this.loadingSubCategories = true;
      try {
        if (this.$call.GetSubCategoriesByCategory && typeof this.$call.GetSubCategoriesByCategory === 'function') {
          this.$call.GetSubCategoriesByCategory(
            categoryId,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.categorySubCategories = dataResponse.data || [];
              } else {
                this.loadDefaultSubCategories(categoryId);
              }
              this.loadingSubCategories = false;
            },
            (errorResource) => {
              console.log('Subcategory API not available yet, using default subcategories');
              this.loadDefaultSubCategories(categoryId);
              this.loadingSubCategories = false;
            }
          );
        } else {
          this.loadDefaultSubCategories(categoryId);
        }
      } catch (error) {
        this.loadDefaultSubCategories(categoryId);
      }
    },

    loadDefaultSubCategories(categoryId) {
      this.categorySubCategories = [];
      this.loadingSubCategories = false;
    },

    openCategoriesDialog() {
      this.categoriesDialog = true;
      this.loadingCategories = true;
      this.loadAccountCategories();
    },

    closeCategoriesDialog() {
      this.categoriesDialog = false;
    },

    openCategoryDialog(item = null) {
      this.categoryEditMode = !!item;
      if (item) {
        this.categoryFormData = {
          ...item,
          subCategories: [],
        };
      } else {
        this.categoryFormData = {
          categoryID: 0,
          categoryCode: '',
          categoryName: '',
          description: '',
          displayOrder: 0,
          isActive: true,
          activeStatus: true,
          subCategories: [],
        };
      }
      this.categoryDialog = true;
    },

    closeCategoryDialog() {
      this.categoryDialog = false;
      this.categoryFormData = {
        categoryID: 0,
        categoryCode: '',
        categoryName: '',
        description: '',
        displayOrder: 0,
        isActive: true,
        activeStatus: true,
        subCategories: [],
      };
      this.$refs.categoryForm?.resetValidation();
    },

    addSubCategory() {
      this.categoryFormData.subCategories.push({
        subCategoryCode: '',
        subCategoryName: '',
        description: '',
        displayOrder: this.categoryFormData.subCategories.length + 1,
        isActive: true,
        activeStatus: true,
      });
    },

    removeSubCategory(index) {
      this.categoryFormData.subCategories.splice(index, 1);
    },

    async saveCategory() {
      if (!this.categoryFormValid) return;

      this.savingCategory = true;
      try {
        if (this.categoryEditMode && this.$call.UpdateAccountCategory && typeof this.$call.UpdateAccountCategory === 'function') {
          this.$call.UpdateAccountCategory(
            this.categoryFormData.categoryID,
            this.categoryFormData,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.$swal.fire('Success', 'Category updated successfully', 'success');
                this.closeCategoryDialog();
                this.loadAccountCategories();
                this.loadAccountInfos();
              } else {
                this.$swal.fire('Error', dataResponse.message || 'Failed to update category', 'error');
              }
              this.savingCategory = false;
            },
            (errorResource) => {
              this.savingCategory = false;
              this.$swal.fire('Error', 'Failed to update category', 'error');
              console.error('Update category error:', errorResource);
            }
          );
        } else if (!this.categoryEditMode && this.$call.CreateAccountCategory && typeof this.$call.CreateAccountCategory === 'function') {
          this.$call.CreateAccountCategory(
            this.categoryFormData,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.$swal.fire('Success', 'Category created successfully', 'success');
                this.closeCategoryDialog();
                this.loadAccountCategories();
                // Also reload account infos in case dropdown options need to be updated
                this.loadAccountInfos();
              } else {
                this.$swal.fire('Error', dataResponse.message || 'Failed to create category', 'error');
              }
              this.savingCategory = false;
            },
            (errorResource) => {
              this.savingCategory = false;
              this.$swal.fire('Error', 'Failed to create category', 'error');
              console.error('Create category error:', errorResource);
            }
          );
        } else {
          // API methods don't exist yet
          this.savingCategory = false;
          this.$swal.fire('Info', 'Category API endpoints are not implemented yet. This feature will be available once the backend is complete.', 'info');
        }
      } catch (error) {
        this.savingCategory = false;
        this.$swal.fire('Error', 'Failed to save category', 'error');
        console.error('Save category exception:', error);
      }
    },

    async confirmDeleteCategory(item) {
      const confirmed = await this.$swal.fire({
        title: 'Are you sure?',
        text: `Delete category: ${item.categoryName}? This will also delete all subcategories.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      });

      if (confirmed.isConfirmed) {
        await this.deleteCategory(item.categoryID);
      }
    },

    async deleteCategory(categoryId) {
      this.loadingDialog = true;
      try {
        if (this.$call.DeleteAccountCategory && typeof this.$call.DeleteAccountCategory === 'function') {
          this.$call.DeleteAccountCategory(
            categoryId,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.$swal.fire('Success', 'Category deleted successfully', 'success');
                this.loadAccountCategories();
                // Also reload account infos in case any account was using this deleted category
                this.loadAccountInfos();
              } else {
                this.$swal.fire('Error', dataResponse.message || 'Failed to delete category', 'error');
              }
              this.loadingDialog = false;
            },
            (errorResource) => {
              this.loadingDialog = false;
              // Check if errorResource has the API error message
              let errorMessage = 'Failed to delete category';
              if (errorResource && errorResource.response && errorResource.response.data && errorResource.response.data.message) {
                errorMessage = errorResource.response.data.message;
              } else if (errorResource && errorResource.message) {
                errorMessage = errorResource.message;
              } else if (typeof errorResource === 'string') {
                errorMessage = errorResource;
              }
              this.$swal.fire('Error', errorMessage, 'error');
              console.error('Delete category error:', errorResource);
            }
          );
        } else {
          // API method doesn't exist yet
          this.loadingDialog = false;
          this.$swal.fire('Info', 'Delete category API endpoint is not implemented yet. This feature will be available once the backend is complete.', 'info');
        }
      } catch (error) {
        this.loadingDialog = false;
        // Extract error message from exception
        let errorMessage = 'Failed to delete category';
        if (error && error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error && error.message) {
          errorMessage = error.message;
        }
        this.$swal.fire('Error', errorMessage, 'error');
        console.error('Delete category exception:', error);
      }
    },

    async viewSubCategories(item) {
      this.selectedCategory = item;
      this.subCategoriesDialog = true;
      // Clear existing data and show skeleton loader
      this.categorySubCategories = [];
      this.loadingSubCategories = true;
      // Load fresh subcategory data
      this.loadSubCategoriesByCategory(item.categoryID);
    },

    closeSubCategoriesDialog() {
      this.subCategoriesDialog = false;
      this.selectedCategory = null;
      this.categorySubCategories = [];
    },

    openSubCategoryDialog(item = null) {
      this.subCategoryEditMode = !!item;
      if (item) {
        this.subCategoryFormData = { ...item };
      } else {
        this.subCategoryFormData = {
          subCategoryID: 0,
          categoryID: this.selectedCategory.categoryID,
          subCategoryCode: '',
          subCategoryName: '',
          description: '',
          displayOrder: 0,
          isActive: true,
          activeStatus: true, // For frontend active status binding
        };
      }
      this.subCategoryDialog = true;
    },

    closeSubCategoryDialog() {
      this.subCategoryDialog = false;
      this.subCategoryFormData = {
        subCategoryID: 0,
        categoryID: 0,
        subCategoryCode: '',
        subCategoryName: '',
        description: '',
        displayOrder: 0,
        isActive: true,
        activeStatus: true, // For frontend active status binding
      };
      this.$refs.subCategoryForm?.resetValidation();
    },

    async saveSubCategory() {
      if (!this.subCategoryFormValid) return;

      this.savingSubCategory = true;
      try {
        if (this.subCategoryEditMode && this.$call.UpdateAccountSubCategory && typeof this.$call.UpdateAccountSubCategory === 'function') {
          this.$call.UpdateAccountSubCategory(
            this.subCategoryFormData.subCategoryID,
            this.subCategoryFormData,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.$swal.fire('Success', 'Subcategory updated successfully', 'success');
                this.closeSubCategoryDialog();
                this.loadSubCategoriesByCategory(this.selectedCategory.categoryID);
                // Reload account info data to reflect subcategory changes
                this.loadAccountInfos();
              } else {
                this.$swal.fire('Error', dataResponse.message || 'Failed to update subcategory', 'error');
              }
              this.savingSubCategory = false;
            },
            (errorResource) => {
              this.savingSubCategory = false;
              this.$swal.fire('Error', 'Failed to update subcategory', 'error');
              console.error('Update subcategory error:', errorResource);
            }
          );
        } else if (!this.subCategoryEditMode && this.$call.CreateAccountSubCategory && typeof this.$call.CreateAccountSubCategory === 'function') {
          this.$call.CreateAccountSubCategory(
            this.subCategoryFormData,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.$swal.fire('Success', 'Subcategory created successfully', 'success');
                this.closeSubCategoryDialog();
                this.loadSubCategoriesByCategory(this.selectedCategory.categoryID);
                // Reload account info data to reflect new subcategory
                this.loadAccountInfos();
              } else {
                this.$swal.fire('Error', dataResponse.message || 'Failed to create subcategory', 'error');
              }
              this.savingSubCategory = false;
            },
            (errorResource) => {
              this.savingSubCategory = false;
              this.$swal.fire('Error', 'Failed to create subcategory', 'error');
              console.error('Create subcategory error:', errorResource);
            }
          );
        } else {
          // API methods don't exist yet
          this.savingSubCategory = false;
          this.$swal.fire('Info', 'Subcategory API endpoints are not implemented yet. This feature will be available once the backend is complete.', 'info');
        }
      } catch (error) {
        this.savingSubCategory = false;
        this.$swal.fire('Error', 'Failed to save subcategory', 'error');
        console.error('Save subcategory exception:', error);
      }
    },

    async confirmDeleteSubCategory(item) {
      const confirmed = await this.$swal.fire({
        title: 'Are you sure?',
        text: `Delete subcategory: ${item.subCategoryName}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      });

      if (confirmed.isConfirmed) {
        await this.deleteSubCategory(item.subCategoryID);
      }
    },

    async deleteSubCategory(subCategoryId) {
      this.loadingDialog = true;
      try {
        if (this.$call.DeleteAccountSubCategory && typeof this.$call.DeleteAccountSubCategory === 'function') {
          this.$call.DeleteAccountSubCategory(
            subCategoryId,
            (dataResponse) => {
              if (dataResponse.status === 0) {
                this.$swal.fire('Success', 'Subcategory deleted successfully', 'success');
                this.loadSubCategoriesByCategory(this.selectedCategory.categoryID);
                // Reload account info data to handle any orphaned account relationships
                this.loadAccountInfos();
              } else {
                this.$swal.fire('Error', dataResponse.message || 'Failed to delete subcategory', 'error');
              }
              this.loadingDialog = false;
            },
            (errorResource) => {
              this.loadingDialog = false;
              // Check if errorResource has the API error message
              let errorMessage = 'Failed to delete subcategory';
              if (errorResource && errorResource.response && errorResource.response.data && errorResource.response.data.message) {
                errorMessage = errorResource.response.data.message;
              } else if (errorResource && errorResource.message) {
                errorMessage = errorResource.message;
              } else if (typeof errorResource === 'string') {
                errorMessage = errorResource;
              }
              this.$swal.fire('Error', errorMessage, 'error');
              console.error('Delete subcategory error:', errorResource);
            }
          );
        } else {
          // API method doesn't exist yet
          this.loadingDialog = false;
          this.$swal.fire('Info', 'Delete subcategory API endpoint is not implemented yet. This feature will be available once the backend is complete.', 'info');
        }
      } catch (error) {
        this.loadingDialog = false;
        // Extract error message from exception
        let errorMessage = 'Failed to delete subcategory';
        if (error && error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error && error.message) {
          errorMessage = error.message;
        }
        this.$swal.fire('Error', errorMessage, 'error');
        console.error('Delete subcategory exception:', error);
      }
    },
    formatDateForAPI(date) {
      if (!date) return null;
      
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;
      
      // Format as YYYY-MM-DD to avoid timezone issues
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style scoped>
.card2 {
  border-radius: 10px !important;
  margin-bottom: 2rem;
  padding-top: 1rem;
}

.btn-layout {
  padding: 14px 24px;
  width: 171px;
  height: 57px;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;
  color: #fff !important;
}

.btn-save {
  color: #fff !important;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 24px;
  width: 140px;
  height: 45px;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
  border-radius: 16px;
  flex: none;
  order: 1;
  flex-grow: 0;
}

.btn-close {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 10px;
  width: 135px;
  height: 45px;
  border: 1px solid #b4b4b4;
  border-radius: 18px;
  flex: none;
  order: 0;
  flex-grow: 0;
}

.btn-export {
  padding: 10px 32px;
  gap: 8px;
  width: 152px;
  height: 57px;
  background: rgba(24, 92, 55, 0.1);
  border: 1px solid #185c37;
  border-radius: 16px;
}

.search-field {
  border-radius: 30px !important;
  z-index: 9999 !important;
}

.filter-field {
  border-radius: 16px !important;
}

.custom-rounded :deep(.v-field) {
  border-radius: 20px !important;
}

.custom-rounded :deep(.v-field__outline) {
  border-radius: 20px !important;
}

.custom-rounded :deep(.v-field__field) {
  border-radius: 20px !important;
}

.filter-rounded :deep(.v-field) {
  border-radius: 16px !important;
}

.filter-rounded :deep(.v-field__outline) {
  border-radius: 16px !important;
}

.filter-rounded :deep(.v-field__field) {
  border-radius: 16px !important;
}

.search-field .v-input__control .v-input__slot {
  min-height: 48px !important;
  background: #f5f5f5 !important;
  box-shadow: none !important;
  border: 1px solid #e0e0e0 !important;
}

.search-field .v-input__prepend-inner {
  margin-right: 8px;
  margin-left: 4px;
}

.search-field .v-icon {
  color: #666;
}

.filter-field .v-input__control .v-input__slot {
  min-height: 48px !important;
  background: #f5f5f5 !important;
  box-shadow: none !important;
  border: 1px solid #e0e0e0 !important;
}

.filter-field .v-input__prepend-inner {
  margin-right: 8px;
  margin-left: 4px;
}

.filter-field .v-icon {
  color: #666;
}

.category-scope-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  min-width: 220px;
}

.customer-scope-chip {
  font-weight: 600;
  letter-spacing: 0;
}

.customer-scope-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 72px;
  padding: 12px 16px;
  border: 1px solid #d8dbe3;
  border-radius: 12px;
  background: #f8f9fb;
}

.customer-scope-title {
  color: #1f1f1f;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
}

.customer-scope-description {
  color: #666;
  font-size: 13px;
  line-height: 1.4;
  margin-top: 2px;
}

:deep(.v-table__wrapper thead tr) {
  background-color: #f5f5f5 !important;
}

:deep(.v-table__wrapper thead th) {
  background-color: #f5f5f5 !important;
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  height: 48px;
}

:deep(.v-data-table-server .v-table thead th) {
  background-color: #f5f5f5 !important;
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  height: 48px;
}

:deep(.v-data-table-server .v-table thead tr th) {
  background-color: #f5f5f5 !important;
}

:deep(.v-data-table) {
  border: 1px solid #e1e1e1;
  border-radius: 24px;
  overflow: hidden;
}

:deep(.v-table) {
  border-radius: 24px;
  overflow: hidden;
}

.v-data-table {
  overflow-x: auto;
}

.table-container {
  overflow-x: auto;
  max-width: 100%;
  position: relative;
}

.sticky-action {
  position: sticky !important;
  right: 0px !important;
  background-color: #fff !important;
  z-index: 10 !important;
}

.sticky-repeat {
  position: sticky !important;
  right: 180px !important;
  background-color: #fff !important;
  z-index: 10 !important;
}

.sticky-status {
  position: sticky !important;
  right: 280px !important;
  background-color: #fff !important;
  z-index: 10 !important;
}

:deep(.my-data-table thead th.sticky-action) {
  position: sticky !important;
  right: 0px !important;
  background-color: #f5f5f5 !important;
  z-index: 15 !important;
}

:deep(.my-data-table thead th.sticky-repeat) {
  position: sticky !important;
  right: 180px !important;
  background-color: #f5f5f5 !important;
  z-index: 15 !important;
}

:deep(.my-data-table thead th.sticky-status) {
  position: sticky !important;
  right: 280px !important;
  background-color: #f5f5f5 !important;
  z-index: 15 !important;
}

:deep(.my-data-table .v-table__wrapper) {
  overflow-x: auto !important;
}

:deep(.my-data-table table) {
  min-width: 1200px;
}

:deep(.v-table .v-table__wrapper > table > tbody > tr > td.sticky-action),
:deep(.v-table .v-table__wrapper > table > thead > tr > th.sticky-action) {
  position: sticky !important;
  right: 0px !important;
  z-index: 10 !important;
}

:deep(.v-table .v-table__wrapper > table > tbody > tr > td.sticky-repeat),
:deep(.v-table .v-table__wrapper > table > thead > tr > th.sticky-repeat) {
  position: sticky !important;
  right: 180px !important;
  z-index: 10 !important;
}

:deep(.v-table .v-table__wrapper > table > tbody > tr > td.sticky-status),
:deep(.v-table .v-table__wrapper > table > thead > tr > th.sticky-status) {
  position: sticky !important;
  right: 280px !important;
  z-index: 10 !important;
}

.data-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  align-items: center;
  max-width: 200px;
  margin: 0 auto;
}

.data-chip {
  font-size: 12px !important;
  color: #666 !important;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 4px 16px;
  gap: 8px;
  width: 136px;
  height: 32px;
  background-color: #f2f2f2 !important;
  border-radius: 100px;
}

:deep(.data-chip .v-chip__content) {
  font-size: 11px;
  font-weight: 500;
  padding: 0 8px;
}

:deep(.v-chip.data-chip) {
  background-color: #f2f2f2 !important;
  color: black !important;
}

.icon-active {
  color: #4caf50 !important;
  opacity: 1;
}

.icon-inactive {
  color: #e0e0e0 !important;
  opacity: 0.5;
}

:deep(.v-icon.icon-active) {
  color: #4caf50 !important;
}

:deep(.v-icon.icon-inactive) {
  color: #bdbdbd !important;
}

/* Dialog Styles */
.add-proactive-dialog {
  border-radius: 16px !important;
}

.dialog-header {
  padding: 20px 16px !important;
}

.dialog-title {
  font-size: 1.25rem !important;
  color: #1f1f1f;
}

.dialog-subtitle {
  color: #898989;
}

.icon-background {
  padding: 8px;
  gap: 10px;
  width: 46px;
  height: 46px;
  background: #dee7f3;
  backdrop-filter: blur(2px);
  border-radius: 100px;
  box-shadow: none;
}

.dialog-content {
  padding: 24px;
  max-height: 70vh;
  overflow-y: auto;
}

.form-section {
  margin-bottom: 24px;
}

.dialog-header-add {
  background: #eeeff3;
}

.header-icon-container {
  width: 48px;
  height: 48px;
  margin-right: 12px;
}

/* Action buttons group styling */
.action-buttons-group {
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
}

.action-btn {
  min-width: 36px !important;
  width: 36px;
  height: 36px;
  border-radius: 6px;
}

.action-btn .v-icon {
  font-size: 14px;
}

/* Status chip styling */
.status-chip {
  font-weight: 500;
  border-radius: 6px;
}

.status-active {
  background: #4caf50 !important;
  color: white !important;
}

.status-inactive {
  background: #f44336 !important;
  color: white !important;
}

/* Category Management Styling */
.btn-category {
  padding: 14px 24px;
  width: 171px;
  height: 57px;
  background: linear-gradient(90deg, #2196f3 0%, #1976d2 100%);
  border-radius: 16px;
  color: #fff !important;
  margin-left: 8px;
}

.btn-category:hover {
  background: linear-gradient(90deg, #1976d2 0%, #1565c0 100%);
  opacity: 0.9;
}

.category-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.category-stat-card {
  flex: 1;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
}

.category-stat-title {
  font-size: 14px;
  opacity: 0.8;
  margin-bottom: 4px;
}

.category-stat-value {
  font-size: 24px;
  font-weight: bold;
}

.categories-table .v-data-table__wrapper {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.subcategories-table .v-data-table__wrapper {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.form-section-title {
  font-size: 16px;
  font-weight: bold;
  color: #1976d2;
  margin-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.subcategory-item {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 12px;
  background-color: #fafafa;
}

.subcategory-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.subcategory-title {
  font-weight: bold;
  color: #424242;
}

.btn-remove-subcategory {
  background-color: #f44336 !important;
  color: white !important;
  min-width: auto;
  width: 32px;
  height: 32px;
}

.btn-add-subcategory {
  background-color: #4caf50 !important;
  color: white !important;
  margin-top: 12px;
}

.btn-view {
  background-color: #2196f3 !important;
  color: white !important;
}

.btn-edit {
  background-color: #ff9800 !important;
  color: white !important;
}

.btn-delete {
  background-color: #f44336 !important;
  color: white !important;
}
/* NEW: Duplicate Button Styles */
.duplicate-btn {
  height: 36px;
  max-width: 36px;
  min-width: 36px;
  padding: 0 8px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: #fff !important;
  border-radius: 8px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3) !important;
}

.duplicate-btn:hover {
  background-color: #6D28D9 !important;
  box-shadow: 0 4px 8px rgba(124, 58, 237, 0.3);
  transform: translateY(-1px);
}

.duplicate-btn .v-icon {
  font-size: 18px;
}

/* Sticky column for Duplicate */
.sticky-duplicate {
  position: sticky !important;
  right: 180px !important;
  background-color: #fff !important;
  z-index: 10 !important;
}
@media (max-width: 960px) and (min-width: 769px) {
  /* Tablet styles */
  .btn-layout,
  .btn-category {
    min-width: 120px;
  }
}

@media (max-width: 768px) {
  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .btn-layout,
  .btn-category {
    width: 100%;
    margin-left: 0;
    margin-right: 0 !important;
  }

  /* Ensure filter buttons are full width on mobile */
  .v-col:last-child .d-flex {
    justify-content: center !important;
  }

  .category-stats {
    flex-direction: column;
  }

  .action-buttons-cell .action-btn {
    margin: 2px;
  }
}

.modern-table :deep(.v-table__wrapper) {
  max-height: 600px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper thead th) {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface, 255, 255, 255));
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar) {
  width: 6px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-track) {
  background: rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb) {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 3px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb:hover) {
  background: rgba(25, 118, 210, 0.5);
}
</style>
